// Shared logic for the part-progress rail (PartProgressTwoLevelRail). It
// consumes the step list served by `part/actualPartProgress/<partId>` (or
// pushed down via props from the monitoring snapshot tick):
//   { technologyName, name, jobReferenceResult ('OK'|'FAIL'|null), deviceStatus,
//     assetName, runningTaskName, programName }
// plus OPTIONAL fields (pending on Sun; everything degrades gracefully):
//   subWorkflowPath — array of subworkflow names, outermost first, on every
//     step that belongs to a WORKFLOW node (Workflow.jsx); nesting can be
//     arbitrarily deep. Single-level fallback: subWorkflowName/subWorkflowId.
//   skipped — Workflow.jsx S26/S30 skip marks; rendered dimmed and excluded
//     from the "active step" computation.
//   deviceTypeName — domain device-type enum of the executing asset (icons).

export const STEP_STATES = {
  PASSED: 'passed',
  FAILED: 'failed',
  ACTIVE: 'active',
  WARNING: 'warning',
  PENDING: 'pending',
  SKIPPED: 'skipped'
}

/** Index of the step the part is currently at (-1 when the route is finished). */
export const getActiveStepIndex = (steps) => {
  return steps.findIndex((step) => !step.jobReferenceResult && !step.skipped)
}

/**
 * Visual state of one step. `warning` mirrors PartProgressStepper: the step is
 * pending on a NON_OPERATIVE device (route blocked), which outranks plain active.
 */
export const getStepState = (step, index, activeIndex) => {
  if (step.skipped) return STEP_STATES.SKIPPED
  if (step.jobReferenceResult === 'FAIL') return STEP_STATES.FAILED
  if (step.jobReferenceResult === 'OK') return STEP_STATES.PASSED
  if (index === activeIndex) {
    return step.deviceStatus === 'NON_OPERATIVE' ? STEP_STATES.WARNING : STEP_STATES.ACTIVE
  }
  return STEP_STATES.PENDING
}

/**
 * Nesting path of a step, outermost first. Supports the full contract
 * (`subWorkflowPath: ['Finishing', 'Cleaning line']` — subworkflows can nest
 * arbitrarily deep, as in Workflow.jsx) and falls back to the single-level
 * fields (`subWorkflowName` / `subWorkflowId`). `[]` = main workflow.
 */
export const getSubWorkflowPath = (step) => {
  if (Array.isArray(step.subWorkflowPath)) {
    return step.subWorkflowPath.filter(Boolean).map(String)
  }
  const single = step.subWorkflowName || step.subWorkflowId || ''
  return single ? [String(single)] : []
}

export const getSubWorkflowLabel = (step) => {
  return getSubWorkflowPath(step).join(' ▸ ')
}

/**
 * Recursive grouping for the multi-level rail: consecutive steps sharing
 * the same subworkflow at each depth become a group entry whose children are
 * grouped again one level deeper. Entries are either
 * `{ type: 'step', item: { step, index, state } }` or
 * `{ type: 'group', name, pathKey, depth, flatItems, children }` where
 * `pathKey` uniquely identifies the group run (path + first step index, so two
 * separate visits to the same subworkflow fold independently).
 */
export const buildProgressTree = (steps) => {
  const activeIndex = getActiveStepIndex(steps)
  const items = steps.map((step, index) => ({ step, index, state: getStepState(step, index, activeIndex) }))

  const buildLevel = (levelItems, depth) => {
    const entries = []
    levelItems.forEach((item) => {
      const path = getSubWorkflowPath(item.step)
      if (path.length <= depth) {
        entries.push({ type: 'step', item })
        return
      }
      const name = path[depth]
      const lastEntry = entries[entries.length - 1]
      if (lastEntry && lastEntry.type === 'group' && lastEntry.name === name) {
        lastEntry.flatItems.push(item)
        return
      }
      entries.push({
        type: 'group',
        name,
        depth,
        pathKey: path.slice(0, depth + 1).join('||') + '@' + item.index,
        flatItems: [item]
      })
    })
    entries.forEach((entry) => {
      if (entry.type === 'group') {
        entry.children = buildLevel(entry.flatItems, depth + 1)
      }
    })
    return entries
  }

  return buildLevel(items, 0)
}

/**
 * Aggregate figures every variant's header shows: completed/total fraction,
 * failure flag and the active step (with its subworkflow name, if any).
 */
export const getProgressSummary = (steps) => {
  const activeIndex = getActiveStepIndex(steps)
  const countable = steps.filter((step) => !step.skipped)
  const completed = countable.filter((step) => step.jobReferenceResult === 'OK').length
  const failed = countable.filter((step) => step.jobReferenceResult === 'FAIL').length
  const activeStep = activeIndex >= 0 ? steps[activeIndex] : {}
  return {
    completed,
    failed,
    total: countable.length,
    finished: activeIndex === -1,
    activeIndex,
    activeStep,
    activeSubWorkflowName: activeIndex >= 0 ? getSubWorkflowLabel(steps[activeIndex]) : ''
  }
}

/** "HAAS-02 · Quenching · PRG-114" — whatever live info the step carries. */
export const formatRunningInfo = (step) => {
  return [step.assetName, step.runningTaskName, step.programName].filter(Boolean).join(' · ')
}
